'use client';

import { Classes } from '@blueprintjs/core';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { LocalStorageKeys } from '../../src/enum/localstorage';

type ThemeContextProps = {
    theme: Theme | null;
    setTheme: (theme: Theme) => void;
}

export enum Theme {
    Light = 0,
    Dark,
};

const ThemeContext = createContext<ThemeContextProps>({
    theme: Theme.Light,
    setTheme: (): void => { },
});

export const getThemeClass = (theme: Theme): string => {
    if (theme === Theme.Dark) {
        return Classes.DARK + ' bp5-dark-body';
    }

    return '';
}

export function ThemeScript() {
    return (
        <script
            dangerouslySetInnerHTML={{
                __html: `
                    (function() {
                        try {
                            var saved = localStorage.getItem("${LocalStorageKeys.Theme}");
                            var prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
                            var isDark = saved === "${Theme.Dark}" || (!saved && prefersDark);

                            document.body.classList.toggle(
                                "${Classes.DARK}",
                                isDark,
                            );

                            document.body.classList.toggle(
                                "bp5-dark-body",
                                isDark,
                            );
                        } catch (e) {}
                    })();
                `,
            }}
        />
    );
}

export default function ThemeProvider({ children }) {
    const [theme, _setTheme] = useState<Theme | null>(null);

    // sets the theme and saves it to local storage
    // so it persists across sessions
    const setTheme = useCallback(
        (newTheme: Theme) => {
            localStorage.setItem(LocalStorageKeys.Theme, newTheme.toString());
            _setTheme(newTheme);
        },
        [_setTheme],
    );

    // initially set the theme based on local storage or system preference
    // we do this in a useEffect to avoid hydration issues and
    // differences between server and client rendering
    useEffect(
        () => {
            var saved = localStorage.getItem(LocalStorageKeys.Theme);
            var prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
            var isDark = saved === Theme.Dark.toString() || (!saved && prefersDark);

            _setTheme(isDark ? Theme.Dark : Theme.Light);
        },
        [],
    );

    const value = { theme, setTheme };

    useEffect(
        () => {
            // need to bail if this runs before initialization,
            // otherwise we get a flash of the wrong theme
            if (theme === null) {
                return;
            }

            document.body.classList.toggle(
                Classes.DARK,
                theme === Theme.Dark,
            );

            document.body.classList.toggle(
                "bp5-dark-body",
                theme === Theme.Dark,
            );
        },
        [theme],
    );

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
